import React from 'react';
import wix from '../../services/wix';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { DEFAULT_CHAT_SETTINGS } from '@wix/inbox-common';
import {
  EDITOR_VIEW_MODE,
  EditorViewMode,
} from '../../constants/editor-view-mode';
import { WINDOW_POSITION } from '../../constants/editor-settings-options';
import { IMAGE_PREVIEW_MODAL_SIZE } from '../../constants/image-preview';
import EditorWidget from '../Widgets/EditorWidget';
import BusinessOnlyWidget from '../Widgets/BusinessOnlyWidget';
import SocialChat from '../Widgets/SocialChat';
import styles from './App.scss';
import classNames from 'classnames';
import DummyWidget from '../Widgets/DummyWidget/DummyWidget';
import { ChatConfigurationProvider } from '@wix/chat-web';
import { withExperiments } from '@wix/wix-experiments-react';
import {
  updateStyleParamsColors,
  updateSiteColors,
  updateEditorSettingsDesign,
  updateEditorSettingsLayout,
  updateEditorSettingsTexts,
  updateViewMode,
  updateIsChatSdkInit,
  expandWidget,
  collapseWidget,
  updateStyleParamsFonts,
  updateEditorSettingsBehaviour,
} from '../../actions';
import { connect } from 'react-redux';
import {
  primaryColorOrDefault,
  textColorOrDefault,
  shouldShowChat as shouldShowChatSelector,
  isExperimentOn,
  messagesFontFamily,
} from '../../reducers';
import chatAppsRegistry from '@wix/chat-apps-registry';
import LeadCaptureForm from '../LeadCaptureForm';
import Coupon from '../Coupon';
import PaymentRequest from '../Payment';
import { APP_PLUGINS } from '../../constants/app-plugins';
import * as biLogger from '../../services/biLogger';
import { InjectedTranslateProps, translate } from 'react-i18next';
import { IMAGE_PREVIEW_PAGE } from '../../constants/endpoints';
import { fedopsLogger, LoadingPhase } from '../../services/fedopsLogger';

export interface AppProps extends InjectedTranslateProps {
  widgetExpanded: boolean;
  visibilitySettings: any;
  locale: string;
  experiments: any;
  styleParamsColorsChange: Function;
  styleParamsFontsChange: Function;
  siteColorsChange: Function;
  editorSettingsDesignChange: Function;
  editorSettingsLayoutChange: Function;
  editorSettingsTextsChange: Function;
  editorSettingsBehaviourChange: Function;
  viewModeChange: Function;
  updateIsChatSdkInit: Function;
  expandWidget: Function;
  collapseWidget: Function;
  messagesFontFamily: string;
  primaryColor: string;
  textColor: string;
  editorSettingsDesign: any;
  editorSettingsLayout: any;
  editorSettingsTexts: any;
  editorSettingsBehaviour: any;
  viewMode: EditorViewMode;
  shouldShowChat: boolean;
  businessChatEnabled: boolean;
  socialChatEnabled: boolean;
  deviceType: string;
  allowInput: boolean;
  disableAutoMessages: boolean;
}

export interface AppState {
  isPublicMember: boolean | undefined;
  isBlockedMember: boolean | undefined;
}

class App extends React.Component<AppProps, AppState> {
  readonly state = {
    isPublicMember: undefined,
    isBlockedMember: undefined,
  };

  async loadSettings() {
    const chatSettings = this.loadChatSettings();

    const styleParams = await Promise.all([
      this.populateColorsFromWix(),
      this.populateFontsFromWix(),
    ]);

    void Promise.all([chatSettings, styleParams]).then(() => {
      fedopsLogger.appLoadingPhaseStart(LoadingPhase.ReceivedSettingsAndStyles);
    });
  }

  async componentDidMount() {
    wix.subscribeForEditModeChanged(({ editMode }) => {
      this.props.updateIsChatSdkInit(false);
      this.props.viewModeChange(editMode);
    });
    if (!chatAppsRegistry.isAppRegistered(APP_PLUGINS.LEAD_CAPTURE_FORM)) {
      chatAppsRegistry.registerApp({
        appName: APP_PLUGINS.LEAD_CAPTURE_FORM,
        component: LeadCaptureForm,
      });
    }
    if (!chatAppsRegistry.isAppRegistered(APP_PLUGINS.COUPONS)) {
      chatAppsRegistry.registerApp({
        appName: APP_PLUGINS.COUPONS,
        component: Coupon,
      });
    }
    if (!chatAppsRegistry.isAppRegistered(APP_PLUGINS.PAYMENT_REQUEST)) {
      chatAppsRegistry.registerApp({
        appName: APP_PLUGINS.PAYMENT_REQUEST,
        component: PaymentRequest,
      });
    }

    await this.loadSettings();

    const isPublicMember = await wix.isPublicMember();
    const isBlockedMember = await wix.isBlockedMember();
    this.setState({ isPublicMember, isBlockedMember });

    wix
      .isResponsive()
      .then((isResponsive) => {
        if (isResponsive) {
          this.props.collapseWidget();
        }
        this.showQabIfNeeded(isPublicMember);
      })
      .catch(() => {});

    fedopsLogger.appLoadingPhaseStart(
      LoadingPhase.ReceivedCurrentMemberDetails,
    );

    if (
      !this.props.businessChatEnabled &&
      (!isPublicMember || isBlockedMember)
    ) {
      void wix.resizeToHidden();
    }

    if (!this.props.businessChatEnabled && isPublicMember && !isBlockedMember) {
      const asRounded = _get(
        this.props.editorSettingsLayout,
        'collapsedAsRounded',
        false,
      );

      void wix.resizeToCollapsedMode(asRounded);
    }

    fedopsLogger.appLoaded();
  }

  async populateFontsFromWix() {
    const { fonts } = await wix.getStyleParams();
    this.props.styleParamsFontsChange(fonts);
  }

  async populateColorsFromWix() {
    const { colors } = await wix.getStyleParams();
    this.props.styleParamsColorsChange(colors);

    const siteColors = await wix.getSiteColors();
    this.props.siteColorsChange(siteColors);
  }

  showQabIfNeeded(isPublicMember: boolean) {
    const {
      businessChatEnabled,
      socialChatEnabled,
      shouldShowChat,
      deviceType,
    } = this.props;
    if (
      deviceType === 'mobile' &&
      shouldShowChat &&
      (businessChatEnabled || (socialChatEnabled && isPublicMember))
    ) {
      wix.updateQab({
        visible: true,
        notifications: false,
        color: this.props.primaryColor,
      });
    }
  }

  checkCollapseFromStyleParams = async () => {
    const { booleans } = await wix.getStyleParams();
    if (booleans.collapsedAsRounded) {
      const layout = {
        ...this.props.editorSettingsLayout,
        collapsedAsRounded: true,
      };
      this.props.editorSettingsLayoutChange(layout);
      wix.setDataPublic('layout', layout).catch(() => {
        console.error('failed to save collapsedAsRounded in data public');
      });
    }
  };

  loadChatSettings = () => {
    const text = this.loadTabSettings('texts');
    const layout = this.loadTabSettings('layout').then(() =>
      this.checkCollapseFromStyleParams(),
    );
    const design = this.loadTabSettings('design');
    const behaviour = this.loadTabSettings('behaviour');

    return Promise.all([text, layout, design, behaviour]);
  };

  loadTabSettings = (tab) =>
    wix.getPublicData(
      tab,
      this.handlePublicDataChanged,
      this.setDefaultSettings,
    );

  handlePublicDataChanged = (publicData) => {
    publicData.design &&
      this.props.editorSettingsDesignChange(publicData.design);
    publicData.layout &&
      this.props.editorSettingsLayoutChange(publicData.layout);
    publicData.texts && this.mergeTextsWithDefaults(publicData.texts);
    publicData.behaviour &&
      this.props.editorSettingsBehaviourChange(publicData.behaviour);
  };

  mergeTextsWithDefaults = (texts) => {
    const combinedTexts = { ...texts };

    // combine "existing" texts with new texts
    // TODO: refactor into a new solution that covers this use-case
    ['offlineTitle', 'offlineMessage', 'offlineChatMessage'].forEach((key) => {
      if (combinedTexts[key] === undefined) {
        combinedTexts[key] = DEFAULT_CHAT_SETTINGS.texts[key];
      }
    });

    this.props.editorSettingsTextsChange(combinedTexts);
  };

  setDefaultSettings = (setting) => {
    const newSetting = {
      [setting]: { ..._get(DEFAULT_CHAT_SETTINGS, setting) },
    };
    this.handlePublicDataChanged(newSetting);
  };

  appClasses = () =>
    classNames({
      [styles.app]: true,
      [styles.mobile]: this.props.deviceType === 'mobile',
      [styles.left]:
        this.props.editorSettingsLayout.position ===
        WINDOW_POSITION.BOTTOM_LEFT,
      [styles.right]:
        this.props.editorSettingsLayout.position !==
        WINDOW_POSITION.BOTTOM_LEFT,
    });

  shouldHideWidgetForSocialChat = ({
    isInSite,
    socialChatEnabled,
    isPublicMember,
    isBlockedMember,
  }) => {
    return (
      isInSite &&
      socialChatEnabled &&
      isPublicMember === undefined &&
      isBlockedMember === undefined
    );
  };

  handleImageClick = ({ url, width, height, fileName, fileSize }) => {
    const { primaryColor, textColor, deviceType } = this.props;
    const isDesktop = deviceType === 'desktop';
    biLogger.imageClickEvent();
    wix.openModal(
      `${window.location.origin}${IMAGE_PREVIEW_PAGE}?url=${encodeURIComponent(
        url,
      )}&fileName=${encodeURIComponent(
        fileName,
      )}&fileSize=${fileSize}&buttonColor=${encodeURIComponent(
        primaryColor,
      )}&textColor=${encodeURIComponent(
        textColor,
      )}&fontFamily=${encodeURIComponent(this.props.messagesFontFamily)}`,
      isDesktop ? IMAGE_PREVIEW_MODAL_SIZE.width : undefined,
      isDesktop ? IMAGE_PREVIEW_MODAL_SIZE.height : undefined,
    );
  };

  render() {
    const { isPublicMember, isBlockedMember } = this.state;
    const {
      editorSettingsDesign,
      editorSettingsLayout,
      editorSettingsTexts,
      editorSettingsBehaviour,
      viewMode,
      socialChatEnabled,
      businessChatEnabled,
      allowInput,
      disableAutoMessages,
      t,
    } = this.props;
    const primaryColor = this.props.primaryColor;
    const textColor = this.props.textColor;
    const isInSite = viewMode === EDITOR_VIEW_MODE.SITE;
    const isInEditor = viewMode === EDITOR_VIEW_MODE.EDITOR;
    const isPreviewAndUnsavedSite =
      viewMode === EDITOR_VIEW_MODE.PREVIEW && !wix.getMetaSiteId();
    if (
      _isEmpty(editorSettingsTexts) ||
      _isEmpty(editorSettingsDesign) ||
      _isEmpty(editorSettingsLayout) ||
      _isEmpty(editorSettingsBehaviour) ||
      !primaryColor ||
      !textColor
    ) {
      return null;
    }
    if (
      this.shouldHideWidgetForSocialChat({
        isInSite,
        socialChatEnabled,
        isPublicMember,
        isBlockedMember,
      })
    ) {
      return null;
    }

    return (
      <ChatConfigurationProvider
        onImageMessageClick={this.handleImageClick as any}
        allowInput={allowInput}
        disableAutoMessages={disableAutoMessages}
      >
        <div data-hook="wrapper" className={this.appClasses()}>
          {isInEditor ? (
            <EditorWidget />
          ) : wix.isInTemplate() || isPreviewAndUnsavedSite ? (
            <DummyWidget
              autoMessage={
                isPreviewAndUnsavedSite
                  ? t('preview.auto-message')
                  : t('template.auto-message')
              }
            />
          ) : socialChatEnabled && isPublicMember && !isBlockedMember ? (
            <SocialChat />
          ) : businessChatEnabled ? (
            <BusinessOnlyWidget />
          ) : (
            <div data-hook="no-chat" />
          )}
        </div>
      </ChatConfigurationProvider>
    );
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    styleParamsColorsChange: (colors) =>
      dispatch(updateStyleParamsColors(colors)),
    styleParamsFontsChange: (fonts) => dispatch(updateStyleParamsFonts(fonts)),
    siteColorsChange: (color) => dispatch(updateSiteColors(color)),
    editorSettingsDesignChange: (design) =>
      dispatch(updateEditorSettingsDesign(design)),
    editorSettingsLayoutChange: (layout) =>
      dispatch(updateEditorSettingsLayout(layout)),
    editorSettingsTextsChange: (texts) =>
      dispatch(updateEditorSettingsTexts(texts)),
    editorSettingsBehaviourChange: (behaviour) =>
      dispatch(updateEditorSettingsBehaviour(behaviour)),
    viewModeChange: (viewMode) => dispatch(updateViewMode(viewMode)),
    updateIsChatSdkInit: (data) => dispatch(updateIsChatSdkInit(data)),
    expandWidget: () => dispatch(expandWidget()),
    collapseWidget: () => dispatch(collapseWidget()),
  };
};

function mapStateToProps(state) {
  return {
    messagesFontFamily: messagesFontFamily(state),
    primaryColor: primaryColorOrDefault(state),
    textColor: textColorOrDefault(state),
    editorSettingsDesign: state.editorSettingsDesign,
    editorSettingsLayout: state.editorSettingsLayout,
    editorSettingsTexts: state.editorSettingsTexts,
    editorSettingsBehaviour: state.editorSettingsBehaviour,
    visibilitySettings: state.visibilitySettings,
    viewMode: state.viewMode,
    locale: state.locale,
    deviceType: state.deviceType,
    shouldShowChat: shouldShowChatSelector(state),
    businessChatEnabled: state.businessChatEnabled,
    socialChatEnabled: state.socialChatEnabled,
    allowInput: state.allowInput,
    disableAutoMessages: state.disableAutoMessages,
    widgetExpanded: state.widgetExpanded,
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withExperiments(translate()(App)));
